import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { StepFooterCta } from '@/components/work-orders/WorkOrderStepFooter'
import type { WorkOrder } from '@/domain/work-order.types'
import type { ResolvedWorkflowSnapshot } from '@/domain/workflow.types'
import { useDesignFiles } from '@/hooks/useDesignFiles'
import { downloadFile, workOrderFilesKey } from '@/hooks/useWorkOrderFiles'
import { DesignFilesList } from './DesignFilesList'
import { DesignSubmitPanel } from './DesignSubmitPanel'
import { IN_PROGRESS_STATUS } from './stage-turn'
import { useDesignSubmitCta } from './useStageCta'

/** The order's submitted design and the files it replaced, for anyone on the order to download. */
export function DesignFilesSection({ workOrder: wo }: { workOrder: WorkOrder }) {
	const { active, inactive, isLoading } = useDesignFiles(wo.orderCode)
	return (
		<SectionCard title="Design files" aria-label="Design files">
			{isLoading ? (
				<ListSkeleton label="Loading design files…" />
			) : (
				<DesignFilesList active={active} inactive={inactive} onDownload={(fileId) => void downloadFile(fileId)} />
			)}
		</SectionCard>
	)
}

export interface DesignHandOver {
	isPanelOpen: boolean
	closePanel: () => void
	/** The footer's Upload design, for the designer. */
	uploadCta: StepFooterCta | null
	/** The panel's Submit for validation. */
	submit: StepFooterCta | null
}

/**
 * The designer's hand-over state. Held by the page rather than the step: the submit moves the
 * order out of In progress optimistically, unmounting the step before the move settles, and the
 * move's toast and the panel's closing must still happen.
 */
export function useDesignHandOver(
	wo: WorkOrder,
	snapshot: ResolvedWorkflowSnapshot | null | undefined,
	isTurnHolder: boolean,
): DesignHandOver {
	const qc = useQueryClient()
	const [isPanelOpen, setPanelOpen] = useState(false)
	const isInProgress = wo.status === IN_PROGRESS_STATUS
	const { drafts } = useDesignFiles(wo.orderCode, isInProgress)
	const submit = useDesignSubmitCta(wo, snapshot, isTurnHolder, drafts.length > 0, () => {
		setPanelOpen(false)
		void qc.invalidateQueries({ queryKey: workOrderFilesKey(wo.orderCode) })
	})
	return {
		isPanelOpen,
		closePanel: () => setPanelOpen(false),
		uploadCta: isInProgress && isTurnHolder ? { label: 'Upload design', onPress: () => setPanelOpen(true) } : null,
		submit,
	}
}

/**
 * In progress: the design submitted so far on the page, and for the designer the panel where
 * each round's drafts are uploaded, validated and submitted.
 */
export function InProgressStep({ workOrder: wo, handOver }: { workOrder: WorkOrder; handOver: DesignHandOver }) {
	return (
		<>
			<DesignFilesSection workOrder={wo} />
			{handOver.uploadCta ? (
				<DesignSubmitPanel
					workOrder={wo}
					isOpen={handOver.isPanelOpen}
					onClose={handOver.closePanel}
					submit={handOver.submit}
				/>
			) : null}
		</>
	)
}
